<template>
  <div class="space-y-6">
    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">加载中...</p>
    </div>

    <template v-else>
      <div v-if="quotas.length > 0" class="space-y-4">
        <div v-for="quota in quotas" :key="quota.id" class="border dark:border-gray-700 rounded-lg p-4">
          <div class="flex justify-between items-start mb-3">
            <div>
              <h4 class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ displayLocalizedText(quota.feature.name) }}</h4>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ quota.feature.appName }} - {{ quota.feature.category }}</p>
            </div>
            <span class="inline-flex items-center rounded-md bg-blue-100 dark:bg-blue-900/30 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400">
              {{ formatQuotaType(quota.quotaType) }}
            </span>
          </div>
          <div class="grid grid-cols-3 gap-4 text-sm mb-3">
            <div>
              <span class="text-gray-500 dark:text-gray-400">已使用：</span>
              <span class="font-medium dark:text-gray-200">{{ quota.usedCount }}</span>
            </div>
            <div>
              <span class="text-gray-500 dark:text-gray-400">剩余：</span>
              <span class="font-medium dark:text-gray-200">{{ formatQuotaLimit(quota.quotaType, quota.remainingCount) }}</span>
            </div>
            <div>
              <span class="text-gray-500 dark:text-gray-400">上限：</span>
              <span class="font-medium dark:text-gray-200">{{ formatQuotaLimit(quota.quotaType, quota.hardLimit) }}</span>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4 text-sm mb-3">
            <div>
              <span class="text-gray-500 dark:text-gray-400">周期开始：</span>
              <span class="font-medium dark:text-gray-200">{{ formatDate(quota.periodStart) }}</span>
            </div>
            <!-- BOOLEAN 类型不显示重置时间 -->
            <div v-if="quota.quotaType !== 'BOOLEAN'">
              <span class="text-gray-500 dark:text-gray-400">重置时间：</span>
              <span class="font-medium dark:text-gray-200">{{ formatDate(quota.resetAt) }}</span>
            </div>
          </div>
          <div class="flex justify-end">
            <button @click="handleResetQuota(quota)" :disabled="actionLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
              重置配额
            </button>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">该用户暂无配额记录</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { User, UserQuota } from '@/types/user/user';
import { getUserQuotasAPI, resetUserQuotaAPI, resetUserQuotaByFeatureCodeAPI } from '@/api/user';
import { displayLocalizedText } from '@/types/membership/localized';
import toast from '@/utils/toaster';

const props = defineProps<{
  user: User | null;
}>();

const emit = defineEmits<{
  (e: 'quota-updated'): void;
}>();

const loading = ref(false);
const actionLoading = ref(false);
const quotas = ref<UserQuota[]>([]);

/**
 * 格式化日期
 */
const formatDate = (date: string | null): string => {
  if (!date) return '未设置';
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 格式化配额类型
 */
const formatQuotaType = (type: string): string => {
  const typeMap: Record<string, string> = {
    DAILY: '每日',
    MONTHLY: '每月',
    YEARLY: '每年',
    BOOLEAN: '布尔',
    UNLIMITED: '无限',
  };
  return typeMap[type] || type;
};

/**
 * 格式化配额限制值
 *
 * @param quotaType 配额类型
 * @param limit 限制值
 * @returns 格式化后的字符串
 */
const formatQuotaLimit = (quotaType: string, limit: number | null): string => {
  // BOOLEAN 类型：null 或 0 表示不可用，1 表示可用
  if (quotaType === 'BOOLEAN') {
    if (limit === null || limit === 0) {
      return '不可用';
    } else {
      return '可用';
    }
  }

  // UNLIMITED 类型：无限制
  if (quotaType === 'UNLIMITED') {
    return '无限';
  }

  // 其他类型：null 表示无限制
  if (limit === null) {
    return '无限';
  }

  return limit.toString();
};

/**
 * 加载配额信息
 */
const loadQuotas = async () => {
  if (!props.user) return;

  loading.value = true;
  try {
    const response = await getUserQuotasAPI(props.user.id);
    quotas.value = response.data || [];
  } catch (error: any) {
    console.error('加载配额信息失败:', error);
    toast.error('加载配额信息失败');
  } finally {
    loading.value = false;
  }
};

/**
 * 重置配额
 */
const handleResetQuota = async (quota: UserQuota) => {
  if (!props.user) return;

  if (!confirm('确定要重置该配额吗？')) return;

  actionLoading.value = true;
  try {
    if (quota.feature && quota.feature.featureCode) {
      // 优先使用 featureCode 重置
      await resetUserQuotaByFeatureCodeAPI(props.user.id, quota.feature.featureCode);
    } else {
      // 降级使用 ID 重置
      await resetUserQuotaAPI(props.user.id, quota.id);
    }
    
    toast.success('配额已重置');
    await loadQuotas();
    emit('quota-updated');
  } catch (error: any) {
    console.error('重置配额失败:', error);
    toast.error(error.message || '重置配额失败');
  } finally {
    actionLoading.value = false;
  }
};

// 初始化加载
watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      loadQuotas();
    }
  },
  { immediate: true }
);
</script>
